import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavSection } from './skipStart';
import {
  SKIP_TARGETS,
  getSkipTarget,
  isSkipOn,
  resolveSkipPath,
  setSkip,
  setSkipTarget,
} from './skipStart';
import { AprScreen } from './AprScreen';
import './StartPage.css';

/**
 * The APR start screen, ported from `apr_template/src/pages/StartPage.tsx`.
 *
 * Copy, structure, class names and icons are the source's, verbatim. `StartPage.css` is
 * the source's own stylesheet copied unchanged — it is fully scoped under `.apr-start`
 * and carries its own colour tokens, so translating 219 lines of it into `sx` would be
 * the kind of "improvement" the playbook forbids, and would lose measurements rather
 * than preserve them.
 *
 * ROUTES are the one thing that changes, per §1.2. Every destination is remapped to its
 * platform equivalent through `R` below. Most do not exist yet: they resolve to the
 * catalogued module route and land on its "not built yet" placeholder rather than being
 * hidden or disabled, per the owner's Q4 seam ruling. Each is repointed as its module
 * lands, and nothing else on this screen changes.
 */

/* ---------- route map: source route -> platform route ---------- */
const R: Record<string, string> = {
  // Built.
  '/customers/applications': '/apr/portfolio/applications',
  '/customers/costing/current-run-rate': '/apr/portfolio/cost',
  // Port list, not built yet — land on the module placeholder.
  '/framework/builder': '/apr',
  '/apr-model': '/apr',
  '/evaluation/qa': '/apr',
  '/evaluation/scoring': '/apr',
  '/evaluation/final-disposition': '/apr',
  '/ai-analysis/run': '/apr',
  '/ai-analysis/results': '/apr',
  '/ai-analysis/repository': '/apr',
  '/ai-analysis/knowledge': '/apr',
  '/ai-analysis/licensing': '/apr',
  '/ai-analysis/licensing/results': '/apr',
  // SEAMS — do-not-port modules. Catalogued platform equivalents.
  '/benefit-realization': '/program_planning',
  '/roadmap/implementation': '/program_planning',
  '/dashboard': '/reporting',
};

const PATHS: Record<string, string> = {
  data: '<ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6"/>',
  framework:
    '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3"/><path d="M2 14h4M10 8h4M18 12h4"/>',
  models: '<path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5M3 17l9 5 9-5"/>',
  qa: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M9.5 9a2.5 2.5 0 0 1 4.6 1.2c0 1.6-2.1 2-2.1 3.3"/><path d="M12 16.5h.01"/>',
  score: '<path d="M3 3v18h18"/><path d="M7 15v3M12 10v8M17 6v12"/>',
  sparkle:
    '<path d="M12 3v4M12 17v4M3 12h4M17 12h4"/><path d="M12 8.5 13.4 11 16 12l-2.6 1L12 15.5 10.6 13 8 12l2.6-1L12 8.5Z"/>',
  results: '<path d="M3 3v18h18"/><path d="m7 14 3-4 3 3 5-7"/>',
  final: '<path d="M12 3l7 3v6c0 4-3 7-7 8-4-1-7-4-7-8V6l7-3Z"/><path d="m9 12 2 2 4-4"/>',
  group:
    '<circle cx="5" cy="6" r="2.4"/><circle cx="19" cy="6" r="2.4"/><circle cx="12" cy="18" r="2.4"/><path d="M7.3 7 10.6 15.8M16.7 7 13.4 15.8M7.4 6h9.2"/>',
  roles:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2.1"/><path d="M5.6 16c.6-1.7 1.9-2.5 3.4-2.5s2.8.8 3.4 2.5"/><path d="M15 9h4M15 13h4"/>',
  cost: '<circle cx="12" cy="12" r="9"/><path d="M12 7v10M14.6 9.2C14 8.5 13 8 12 8c-1.5 0-2.6.8-2.6 2s1.1 1.7 2.6 2 2.6.9 2.6 2-1.1 2-2.6 2c-1 0-2-.5-2.6-1.2"/>',
  benefit: '<path d="M3 17l6-6 4 4 8-8"/><path d="M17 7h4v4"/>',
  roadmap:
    '<path d="M5 4v16"/><circle cx="5" cy="8" r="1.6"/><circle cx="5" cy="16" r="1.6"/><path d="M9 8h10M9 16h6"/>',
  dash: '<rect x="3" y="3" width="8" height="10" rx="1.5"/><rect x="13" y="3" width="8" height="6" rx="1.5"/><rect x="13" y="12" width="8" height="9" rx="1.5"/><rect x="3" y="16" width="8" height="5" rx="1.5"/>',
  fact: '<rect x="6" y="4" width="12" height="17" rx="2"/><path d="M9 4a3 3 0 0 1 6 0"/><path d="m9.5 13 1.4 1.4 3-3"/>',
  license: '<path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2V3Z"/><path d="M9 8h6M9 12h6"/>',
  hub: '<circle cx="18" cy="5" r="2.4"/><circle cx="6" cy="12" r="2.4"/><circle cx="18" cy="19" r="2.4"/><path d="m8.2 10.8 7.5-4.4M8.2 13.2l7.5 4.4"/>',
  arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  back: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
  chat: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11"/>',
  grid: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/>',
  compass:
    '<circle cx="12" cy="12" r="3.2"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l2.5 2.5M16.5 16.5 19 19M19 5l-2.5 2.5M7.5 16.5 5 19"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  seed: '<path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>',
};

const Ic = ({ name, size = 20, sw = 2 }: { name: string; size?: number; sw?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: PATHS[name] || '' }}
  />
);
const Arrow = () => <Ic name="arrow" size={17} sw={2.4} />;

/* ---------- types ---------- */
type Crumb = [string, string?]; // [label, route?]
interface Step {
  n: string;
  sub: string;
  badge: string;
  icon: string;
  title: string;
  desc: string;
  does: string[];
  where: Crumb[];
  route: string;
  chip?: string;
  intro?: string;
  gauge?: boolean;
  _optional?: boolean;
}
interface Group {
  label: string;
  optional: boolean;
  steps: Step[];
}
type Tile = [string, string, string, string]; // icon, title, subtitle, route

/* ---------- the journey, grouped by the app's real sections ---------- */
const GROUPS: Group[] = [
  {
    label: 'Set up · Data & framework',
    optional: false,
    steps: [
      {
        n: 'Portfolio',
        sub: 'Applications & costs',
        badge: 'Foundation',
        icon: 'data',
        title: 'Load your applications and their costs',
        desc: 'Start by bringing in your application inventory and what each one costs to run. Import a spreadsheet or add applications by hand — this is the foundation everything else builds on.',
        does: [
          'Import or enter your application list',
          'Attach current run-rate cost to each application',
          'Keep loading and extending this data as you learn more',
        ],
        where: [
          ['Portfolio'],
          ['Application Master Data', '/customers/applications'],
          ['Current Run Rate', '/customers/costing/current-run-rate'],
        ],
        route: '/customers/applications',
        chip: 'Load once, then extend at any time — nothing is locked.',
      },
      {
        n: 'Scoring framework',
        sub: 'Dimensions & bands',
        badge: 'Configure',
        icon: 'framework',
        title: 'Set up your scoring framework',
        desc: 'Decide how applications get scored and how scores become recommendations. The framework holds your dimensions, weights, and disposition bands — so results reflect how your organization defines value. It ships with sensible defaults you can tune at any time.',
        does: [
          'Configure dimensions, components & questions',
          'Set attribute weights and the scoring model',
          'Define disposition bands (e.g. Retire / Re-Platform / Re-Design / Retain)',
        ],
        where: [
          ['Settings'],
          ['Assessment Framework', '/framework/builder'],
          ['Disposition Matrix', '/framework/builder'],
        ],
        route: '/framework/builder',
        chip: 'Fully configurable — your dimensions, your score bands, your dispositions.',
      },
    ],
  },
  {
    label: 'Application Evaluation',
    optional: false,
    steps: [
      {
        n: 'APR models & run',
        sub: 'Scope & execute',
        badge: 'Scope',
        icon: 'models',
        title: 'Create, group and run APR models',
        desc: 'An APR model is a scope of applications you rationalize together — a wave, a business unit, a vendor family. Group them, schedule when each runs, and execute. Create as many as you need; expand any of them later.',
        does: [
          'Create a model and add its applications',
          'Schedule and execute each model',
          'Manage as many rationalization models as you need',
        ],
        where: [['Application Evaluation'], ['APR Model & Execution', '/apr-model']],
        route: '/apr-model',
        chip: 'As many models as you need · each one expandable.',
      },
      {
        n: 'Owner Q&A',
        sub: 'First-hand input',
        badge: 'Evidence',
        icon: 'qa',
        title: 'Ask the people who know each application',
        desc: 'Application owners answer a short, targeted questionnaire about each application. Their answers give every disposition a defensible, first-hand basis.',
        does: [
          'Invite application owners into the portfolio Q&A',
          'Collect structured answers per application',
          'Feed the answers into the initial scoring',
        ],
        where: [['Initial Disposition'], ['Application Portfolio Q&A', '/evaluation/qa']],
        route: '/evaluation/qa',
        chip: 'Every score traces back to an owner’s own answer.',
      },
      {
        n: 'Initial scoring',
        sub: 'First read',
        badge: 'Scoring',
        icon: 'score',
        title: 'See the initial portfolio scoring',
        desc: 'The Q&A answers roll up — through your scoring framework — into an initial score for every application, a first data-driven read on where each one sits before the deeper AI analysis. Share it as a report.',
        does: [
          'Review initial scores across the portfolio',
          'Spot obvious retire / retain candidates early',
          'Export data & reports for stakeholders',
        ],
        where: [
          ['Initial Portfolio Scoring', '/evaluation/scoring'],
          ['↳ Scoring Framework', '/framework/builder'],
        ],
        route: '/evaluation/scoring',
        chip: 'Scores follow the framework you configured — tune it any time.',
      },
      {
        n: 'AI analysis',
        sub: 'From your content',
        badge: 'Augment',
        icon: 'sparkle',
        title: 'Turn your documents into disposition analysis',
        desc: 'Upload the material you already have — architecture docs, contracts, spend reports, meeting notes — into the Knowledge Library, then run an AI analysis. The AI reads your content and proposes dispositions, cost ranges, and a roadmap.',
        does: [
          'Add your documents to the Knowledge Library',
          'Run an AI analysis over a model',
          'Get proposed dispositions, costs & rationale',
        ],
        where: [
          ['Knowledge Library', '/ai-analysis/knowledge'],
          ['Run Analysis', '/ai-analysis/run'],
        ],
        route: '/ai-analysis/run',
        gauge: true,
      },
      {
        n: 'Results & repository',
        sub: 'Review & version',
        badge: 'Repository',
        icon: 'results',
        title: 'Review, version and pin the results',
        desc: 'Every run lands in Analysis Results, and each application keeps a full history in the Analysis Repository. Compare versions, augment them, and pin the one you trust as the current view.',
        does: [
          'Read AI-proposed dispositions, costs & rationale',
          'Keep a versioned history per application',
          'Pin the version that becomes the decision of record',
        ],
        where: [
          ['Analysis Results', '/ai-analysis/results'],
          ['Analysis Repository', '/ai-analysis/repository'],
        ],
        route: '/ai-analysis/results',
        chip: 'Nothing is thrown away — every analysis is versioned and pinnable.',
      },
      {
        n: 'Final disposition',
        sub: 'Commit the call',
        badge: 'Decision',
        icon: 'final',
        title: 'Lock the final disposition',
        desc: 'When you’re confident, commit the final disposition for each application. This is the decision of record that everything downstream builds on — and it’s always your call, not the tool’s.',
        does: [
          'Confirm the disposition per application',
          'Capture the rationale behind the decision',
          'Hand a clean, agreed decision set to planning',
        ],
        where: [['Application Evaluation'], ['Final Disposition', '/evaluation/final-disposition']],
        route: '/evaluation/final-disposition',
        chip: 'The hand-off point into planning — only proceed if you choose to.',
      },
    ],
  },
  {
    label: 'License Intelligence',
    optional: true,
    steps: [
      {
        n: 'Licensing analysis',
        sub: 'Vendor renegotiation',
        badge: 'Optional',
        icon: 'license',
        title: 'Turn contracts into a licensing renegotiation case',
        intro:
          'License Intelligence is a separate, optional track — you only step into it if you want to negotiate licence spend down. It runs off the same applications and documents you already loaded.',
        desc: 'Flag the applications you pay licence fees for, attach the vendor contract or renewal quote, and choose the negotiation playbook (Microsoft, Salesforce, SAP, Oracle, Workday …) — or generate a new one for a vendor you don’t have yet. The AI reads your contract against the playbook and proposes concrete renegotiation moves with Low / Mid / High savings tiers.',
        does: [
          'Select the applications you license and attach their contracts',
          'Pick or generate the vendor negotiation prompt',
          'Get renegotiation moves with Low / Mid / High savings',
        ],
        where: [['License Intelligence'], ['Licensing Analysis', '/ai-analysis/licensing']],
        route: '/ai-analysis/licensing',
        chip: 'Optional — driven by your contract and the vendor playbook, not fixed bands.',
      },
      {
        n: 'Savings & documents',
        sub: 'Board-ready packs',
        badge: 'Optional',
        icon: 'results',
        title: 'Review savings and generate the negotiation pack',
        desc: 'Every run lands in Licensing Results with its savings tiers per application. Select the applications and versions you want, then generate polished, dated Word briefs and PowerPoint decks — per application, ready to take into the negotiation.',
        does: [
          'Review Low / Mid / High savings per application',
          'Select the applications and versions to export',
          'Generate dated Word & PowerPoint negotiation packs',
        ],
        where: [['License Intelligence'], ['Licensing Results', '/ai-analysis/licensing/results']],
        route: '/ai-analysis/licensing/results',
        chip: 'Optional — a self-contained savings story you can run any time.',
      },
    ],
  },
  // The Integration group of the walkthrough was here. Integration is a do-not-port
  // module (CLAUDE.md §1.6) and the owner has removed it from this screen, so the tour no
  // longer walks users into a section this platform does not have.
  {
    label: 'The outcome',
    optional: false,
    steps: [
      {
        n: 'Executive dashboard',
        sub: 'The whole picture',
        badge: 'Outcome',
        icon: 'dash',
        title: 'See the whole portfolio in one view',
        desc: 'The Executive Dashboard brings scores, dispositions, costs and benefits into a single leadership view — the payoff for the work, and the picture you present to the board.',
        does: [
          'Portfolio dispositions & scores at a glance',
          'Cost and benefit totals across the plan',
          'A board-ready view of the whole exercise',
        ],
        where: [['Executive Dashboard', '/dashboard']],
        route: '/dashboard',
        chip: 'The story of the entire exercise, on one screen.',
      },
    ],
  },
];
const FLAT: Step[] = GROUPS.flatMap((g) => g.steps.map((s) => ({ ...s, _optional: g.optional })));
const TOTAL = FLAT.length;

const gaugeVerdict = (v: number): { label: string; color: string; caption: string } => {
  if (v < 12)
    return {
      label: 'No analysis',
      color: 'var(--s-bad)',
      caption: 'Empty Knowledge Library — the AI has nothing to reason from.',
    };
  if (v < 40)
    return {
      label: 'Thin & low-confidence',
      color: 'var(--s-warn)',
      caption: 'A few documents — expect broad, caveated suggestions only.',
    };
  if (v < 72)
    return {
      label: 'Solid',
      color: 'var(--s-accent)',
      caption: 'Good coverage — dispositions with costs and a workable rationale.',
    };
  return {
    label: 'High-confidence',
    color: 'var(--s-good)',
    caption: 'Rich context — specific, defensible dispositions, costs & roadmap.',
  };
};

const QUICK_TILES: Tile[] = [
  [
    'framework',
    'Scoring Framework',
    'Dimensions, weights & disposition bands',
    '/framework/builder',
  ],
  ['data', 'Portfolio', 'Applications & current run-rate costs', '/customers/applications'],
  [
    'fact',
    'Application Evaluation',
    'Q&A · scoring · AI analysis · final disposition',
    '/apr-model',
  ],
  [
    'license',
    'License Intelligence',
    'Renegotiation & licensing savings',
    '/ai-analysis/licensing',
  ],
  ['dash', 'Executive Dashboard', 'Portfolio outcomes at a glance', '/dashboard'],
];

/**
 * Where "Contact your APR advisor" goes.
 *
 * The source's button is inert — it carries no handler at all. The owner asked for it to
 * reach support@talogent.com, so it becomes a mail link with a subject naming the product,
 * which is the difference between a support inbox that can triage and one that cannot.
 */
const ADVISOR_MAILTO =
  'mailto:support@talogent.com' +
  '?subject=' + encodeURIComponent('APR — request for assistance') +
  '&body=' + encodeURIComponent(
    'Tell us what you need help with and we will get back to you.\n\n' +
      '(Sent from Application Portfolio Rationalization)\n',
  );

const LAUNCH: { label: string; optional?: boolean; items: Tile[] }[] = [
  {
    label: 'Set up',
    items: [
      ['data', 'Portfolio', 'Applications & current run-rate costs', '/customers/applications'],
    ],
  },
  {
    label: 'Application Evaluation',
    items: [
      ['models', 'APR Models', 'Create, group & run models', '/apr-model'],
      ['qa', 'Application Q&A', 'Owner questionnaires that drive scoring', '/evaluation/qa'],
      ['sparkle', 'Run Analysis', 'AI disposition analysis from your content', '/ai-analysis/run'],
      [
        'results',
        'Results & Repository',
        'Review, version & pin dispositions',
        '/ai-analysis/results',
      ],
      [
        'final',
        'Final Disposition',
        'Commit the decision of record',
        '/evaluation/final-disposition',
      ],
    ],
  },
  {
    label: 'License Intelligence',
    optional: true,
    items: [
      [
        'license',
        'Licensing Analysis',
        'Vendor negotiation prompts & renegotiation runs',
        '/ai-analysis/licensing',
      ],
      [
        'results',
        'Licensing Results',
        'Savings tiers · Word & PowerPoint packs',
        '/ai-analysis/licensing/results',
      ],
    ],
  },
  {
    label: 'Outcome',
    items: [['dash', 'Executive Dashboard', 'The whole portfolio in one view', '/dashboard']],
  },
];


export function AprStartPage({
  firstName = '',
  sections = [],
}: {
  firstName?: string;
  /** APR's own nav tree, so "Open X now" resolves a bucket to its first screen. */
  sections?: readonly NavSection[];
}) {
  const navigate = useNavigate();
  const [view, setView] = useState<'start' | 'wizard' | 'experienced'>('start');
  const [idx, setIdx] = useState(0);
  const [gauge, setGauge] = useState(8);
  const [done, setDone] = useState(false);
  const [skip, setSkipOn] = useState<boolean>(() => isSkipOn());
  const [skipTarget, setTarget] = useState<string>(() => getSkipTarget());

  const go = (route: string) => navigate(R[route] ?? route);
  const toStep = (i: number) => {
    setIdx(Math.max(0, Math.min(TOTAL - 1, i)));
    setDone(false);
  };

  const toggleSkip = (on: boolean) => {
    setSkipOn(on);
    setSkip(on);
    // Turning it on writes the target too, so the preference is never half-saved: a
    // flag with no destination is what "skip" used to be, and it did nothing.
    if (on) setSkipTarget(skipTarget);
  };

  const chooseTarget = (path: string) => {
    setTarget(path);
    setSkipTarget(path);
  };

  const targetLabel =
    SKIP_TARGETS.find((t) => t.path === skipTarget)?.label ?? SKIP_TARGETS[0].label;

  const renderStart = () => (
    <div className="view">
      <div className="hero">
        <span className="eyebrow">Welcome{firstName ? `, ${firstName}` : ''}</span>
        <h1>Rationalize your application portfolio — with a path, not a puzzle.</h1>
        <p>
          A guided, evidence-based way to decide what to retire, replatform, redesign, or retain —
          and to see what each move costs before you commit.
        </p>
      </div>

      <div className="paths">
        <button
          className="path primary"
          onClick={() => {
            setView('wizard');
            toStep(0);
          }}
        >
          <span className="mode">Guided walkthrough · recommended</span>
          <span className="ic">
            <Ic name="compass" size={22} />
          </span>
          <h3>First time with APR</h3>
          <span className="lead">
            New to application rationalization? Relax — I&apos;ll walk you through the whole journey
            one friendly step at a time: what each stage does, where to find it in the app, and why
            it matters. No prior experience needed.
          </span>
          <ul className="feats">
            <li>
              <Ic name="check" size={15} sw={3} /> A short, plain-language tour of every stage
            </li>
            <li>
              <Ic name="check" size={15} sw={3} /> See exactly where each step lives in the app
            </li>
            <li>
              <Ic name="check" size={15} sw={3} /> Stop, skip or jump around at any time
            </li>
          </ul>
          <span className="go">
            Start the guided walkthrough <Arrow />
          </span>
        </button>
        <button className="path" onClick={() => setView('experienced')}>
          <span className="mode">Straight to your workspace</span>
          <span className="ic">
            <Ic name="grid" size={22} />
          </span>
          <h3>I&apos;ve done this before</h3>
          <span className="lead">
            Know the APR process already? Skip the tour and jump straight to any section —
            framework, evaluation, licensing or the dashboard — in a single click.
          </span>
          <span className="go">
            Go to my workspace <Arrow />
          </span>
        </button>
      </div>

      <div className={'skipbar' + (skip ? ' on' : '')}>
        <label className="switch">
          <input type="checkbox" checked={skip} onChange={(e) => toggleSkip(e.target.checked)} />
          <span className="sw">
            <span className="knob"></span>
          </span>
          <span className="lab">Skip this welcome screen next time</span>
        </label>
        {/* The destination is CHOSEN, not assumed. It used to read "straight to Framework
            setup, then on to Master Data" — a sentence describing a route the app never
            took, because nothing read the preference at all. The four options are the top
            level of this product's own menu. */}
        <span className="txt">
          {skip ? (
            <>
              Saved — next sign-in opens straight to <b>{targetLabel}</b>. Take me to:
            </>
          ) : (
            <>
              Turn this on and you&apos;ll skip this welcome screen — signing straight into the
              section you choose. Turn it off here at any time to bring the screen back.
            </>
          )}
        </span>

        <label className="skip-target">
          <span className="skip-target-lab">Open</span>
          <select
            value={skipTarget}
            onChange={(e) => chooseTarget(e.target.value)}
            aria-label="Screen to open when the welcome screen is skipped"
          >
            {SKIP_TARGETS.map((t) => (
              <option key={t.path} value={t.path}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        {/* Resolve the bucket to its first SCREEN — navigating to the bucket itself
            lands on a list of cards, which is what skipping exists to avoid. */}
        {skip && (
          <button
            className="btn btn-primary go-now"
            onClick={() => navigate(resolveSkipPath(sections, skipTarget))}
          >
            Open {targetLabel} now <Arrow />
          </button>
        )}
      </div>

      <div className="block-label">
        <h2>Jump straight to</h2>
        <span className="rule"></span>
      </div>
      <div className="quick">
        {QUICK_TILES.map(([ic, t, s, r]) => (
          <button key={t} className="tile" onClick={() => go(r)}>
            <span className="ti">
              <Ic name={ic} />
            </span>
            <span>
              <b>{t}</b>
              <span>{s}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="support">
        <span className="ic">
          <Ic name="chat" />
        </span>
        <p>
          <b>Need Assistance?</b> Our team runs working sessions with you — the platform guides you,
          and you&apos;re never on your own.
        </p>
        {/* Opens the user's own mail client, pre-addressed to support@talogent.com with a
            subject that says where it came from (owner's ruling, 2026-08-04). A mailto
            rather than a server-sent message: the point of a contact button is that the
            person writes what they need, and a silent server send gives them no copy of
            what was sent and nowhere for the reply to land. */}
        <a className="btn btn-ghost cta" href={ADVISOR_MAILTO}>
          Contact your APR advisor
        </a>
      </div>
    </div>
  );

  // `idx` is clamped by `toStep`, so this is always defined. The platform builds with
  // noUncheckedIndexedAccess; the source does not.
  const step = FLAT[idx]!;
  const pct = Math.round(((idx + 1) / TOTAL) * 100);
  const renderWizard = () => (
    <div className="view">
      <button className="back-link" onClick={() => setView('start')}>
        <Ic name="back" size={16} sw={2.2} /> Back to welcome screen
      </button>
      <div className="wiz-top">
        <div>
          <span className="eyebrow">Your guided walkthrough</span>
          <h1>
            {firstName
              ? `Welcome, ${firstName} — here's how APR works, step by step`
              : `Welcome — here's how APR works, step by step`}
          </h1>
        </div>
        <div className="progress-wrap">
          <div className="progress-meta">
            <span>{done ? 'Complete' : `Step ${idx + 1} of ${TOTAL}`}</span>
            <span>{done ? 100 : pct}%</span>
          </div>
          <div className="progress">
            <span className="bar" style={{ width: (done ? 100 : pct) + '%' }}></span>
          </div>
        </div>
      </div>

      <div className="wiz-help">
        <span className="ic">
          <Ic name="users" size={18} />
        </span>
        <p style={{ margin: 0 }}>
          <b>You're never doing this alone.</b> At any step, our team can run a working session with
          you — in person or remote — and a specialized <b>License Intelligence</b> track is there
          when you need it.
        </p>
      </div>

      <div className="wiz">
        <nav className="rail" aria-label="Steps">
          {GROUPS.map((g) => (
            <Fragment key={g.label}>
              <div className="rgroup">
                <span className="glabel">{g.label}</span>
                {g.optional && <span className="opt">Optional</span>}
                <span className="gline"></span>
              </div>
              {g.steps.map((s) => {
                const i = FLAT.findIndex((f) => f.title === s.title);
                const cls =
                  'rstep' +
                  (!done && i === idx ? ' current' : '') +
                  (done || i < idx ? ' done' : '');
                return (
                  <button key={s.title} className={cls} onClick={() => toStep(i)}>
                    <span className="num">{i + 1}</span>
                    <span>
                      <b>{s.n}</b>
                      <small>{s.sub}</small>
                    </span>
                  </button>
                );
              })}
            </Fragment>
          ))}
        </nav>

        <div className="panel">
          {done ? (
            <div className="finish">
              <div className="seal">
                <Ic name="check" size={30} sw={2.2} />
              </div>
              <h2>That's the whole journey</h2>
              <p>
                You now know how the pieces fit — from raw application data all the way to a
                final, evidenced disposition. Nothing has to happen at once: load what you have,
                and extend it as you go. One section is optional and there whenever you want it:{' '}
                <b>License Intelligence</b>, to negotiate licence spend down.
              </p>
              <div className="recap">
                {[
                  'Portfolio & costs',
                  'Scoring framework',
                  'APR models & run',
                  'Owner Q&A',
                  'Initial scoring',
                  'AI analysis',
                  'Results & repository',
                  'Final disposition',
                  'License Intelligence · optional',
                  'Savings & documents',
                  'Executive dashboard',
                ].map((r) => (
                  <span key={r}>{r}</span>
                ))}
              </div>
              <div className="btns">
                <button className="btn btn-primary" onClick={() => go('/customers/applications')}>
                  Start with Portfolio <Arrow />
                </button>
                <button className="btn btn-ghost" onClick={() => setView('experienced')}>
                  Explore on my own
                </button>
              </div>
            </div>
          ) : (
            <div className="step">
              {step.intro && (
                <div className="intro-note">
                  <Ic name="seed" size={18} />
                  <span>{step.intro}</span>
                </div>
              )}
              <span className={'badge' + (step._optional ? ' opt' : '')}>
                {step._optional ? 'Optional · ' : ''}
                {step.badge}
              </span>
              <div className="pic">
                <Ic name={step.icon} size={26} />
              </div>
              <h2>{step.title}</h2>
              <p className="desc">{step.desc}</p>
              <ul className="do-list">
                {step.does.map((x) => (
                  <li key={x}>
                    <span className="tick">
                      <Ic name="check" size={12} sw={3} />
                    </span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              {step.chip && (
                <div className="chip-note">
                  <Ic name="info" size={16} />
                  {step.chip}
                </div>
              )}
              {step.gauge &&
                (() => {
                  const v = gaugeVerdict(gauge);
                  return (
                    <div className="gauge-card">
                      <p className="gauge-emph">
                        <strong>The analysis is only as good as what you feed it.</strong> With no
                        content, there is no meaningful analysis. The more relevant material you
                        provide, the stronger and more confident the results.
                      </p>
                      <div className="g-head">
                        <b>Try it: how much content have you provided?</b>
                      </div>
                      <div className="g-track">
                        <span className="g-fill" style={{ width: Math.max(4, gauge) + '%' }}></span>
                      </div>
                      <input
                        type="range"
                        className="slider"
                        min={0}
                        max={100}
                        value={gauge}
                        onChange={(e) => setGauge(+e.target.value)}
                        aria-label="Amount of content provided"
                      />
                      <div className="g-readout">
                        <span className="g-verdict" style={{ color: v.color }}>
                          {v.label}
                        </span>
                        <span className="g-pct">{gauge}%</span>
                      </div>
                      <div className="g-caption">{v.caption}</div>
                    </div>
                  );
                })()}
              <div className="where">
                <span className="lbl">In the app</span>
                {step.where.map(([label, route], i) => (
                  <Fragment key={label}>
                    {i > 0 && <span className="dot">›</span>}
                    <button className="crumb" onClick={() => go(route || step.route)}>
                      {label}
                    </button>
                  </Fragment>
                ))}
              </div>
              <div className="controls">
                <button
                  className="btn btn-ghost"
                  onClick={() => (idx > 0 ? toStep(idx - 1) : setView('start'))}
                >
                  Back
                </button>
                <span className="step-tag">
                  Step {idx + 1} of {TOTAL}
                </span>
                <span className="spacer"></span>
                <button
                  className="btn btn-primary"
                  onClick={() => (idx < TOTAL - 1 ? toStep(idx + 1) : setDone(true))}
                >
                  {idx === TOTAL - 1 ? 'Finish' : 'Next'} <Arrow />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ---------- RETURNING USER ---------- */
  const renderExperienced = () => (
    <div className="view">
      <button className="back-link" onClick={() => setView('start')}>
        <Ic name="back" size={16} sw={2.2} /> Back to welcome screen
      </button>
      <div className="hero" style={{ paddingBottom: 20 }}>
        <span className="eyebrow">Welcome back{firstName ? `, ${firstName}` : ''}</span>
        <h1 style={{ fontSize: 40 }}>Jump back in — starting with your framework.</h1>
        <p>
          You already know the APR process. Confirm your scoring framework first — it may match how
          you&apos;ve run APR before, or it may be new for this engagement — then go straight to
          whatever section you need.
        </p>
      </div>

      <button className="fw-hero" onClick={() => go('/framework/builder')}>
        <span className="fic">
          <Ic name="framework" size={26} />
        </span>
        <div>
          <span className="badge">Start here</span>
          <h3>Confirm your scoring framework</h3>
          <p>
            Dimensions, weights and disposition bands — this is what everything downstream scores
            against. Reuse a framework you already trust, or reshape it for this client. Worth a
            look before anything else.
          </p>
          <span className="go">
            Open Scoring Framework <Arrow />
          </span>
        </div>
      </button>

      {LAUNCH.map((g) => (
        <div key={g.label}>
          <div className="block-label">
            <h2>{g.label}</h2>
            {g.optional && <span className="opt">Optional</span>}
            <span className="rule"></span>
          </div>
          <div className="quick">
            {g.items.map(([ic, t, s, r]) => (
              <button key={t} className="tile" onClick={() => go(r)}>
                <span className="ti">
                  <Ic name={ic} />
                </span>
                <span>
                  <b>{t}</b>
                  <span>{s}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <AprScreen>
      <div className="page apr-start">
        <div className="inner">
          {view === 'start'
            ? renderStart()
            : view === 'wizard'
              ? renderWizard()
              : renderExperienced()}
        </div>
      </div>
    </AprScreen>
  );
}
